import { useEffect } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useGenerateProductDescription,
  useUpdateProduct,
} from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'

const DESCRIPTION_REQUIRED = 'description_required'

const productDescriptionFormSchema = z.object({
  description: z.string().trim().min(1, { message: DESCRIPTION_REQUIRED }),
})

type ProductDescriptionFormValues = z.infer<typeof productDescriptionFormSchema>

type V2ProductDescriptionEditDialogProps = {
  productId: string
  description: string | null | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function V2ProductDescriptionEditDialog({
  productId,
  description,
  open,
  onOpenChange,
}: V2ProductDescriptionEditDialogProps) {
  const { t } = useTranslation('common')
  const { mutateAsync: updateProduct, isPending: isSaving } = useUpdateProduct()
  const {
    mutateAsync: generateDescription,
    isPending: isGenerating,
  } = useGenerateProductDescription(productId)
  const isPending = isSaving || isGenerating

  const form = useForm<ProductDescriptionFormValues>({
    resolver: zodResolver(productDescriptionFormSchema),
    defaultValues: {
      description: description ?? '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        description: description ?? '',
      })
    }
  }, [open, description, form])

  async function onGenerateFromSources() {
    try {
      const result = await generateDescription()
      form.setValue('description', result.description, {
        shouldDirty: true,
        shouldValidate: true,
      })
    } catch {
      toast.error(
        t(
          'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.generateFailed'
        )
      )
    }
  }

  async function onSubmit(values: ProductDescriptionFormValues) {
    try {
      await updateProduct({
        productId,
        description: values.description.trim(),
      })
      onOpenChange(false)
      toast.success(
        t(
          'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.saveSuccess'
        )
      )
    } catch {
      toast.error(
        t(
          'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.saveFailed'
        )
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {t(
              'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.title'
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.description'
            )}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <div className='flex items-center justify-between gap-2'>
                    <FormLabel className='mb-0'>
                      {t(
                        'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.fieldLabel'
                      )}
                    </FormLabel>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='h-7 shrink-0 gap-1 px-2 text-xs'
                      onClick={() => {
                        void onGenerateFromSources()
                      }}
                      disabled={isPending}
                    >
                      {isGenerating ? (
                        <Loader2
                          className='size-3.5 animate-spin'
                          aria-hidden
                        />
                      ) : (
                        <Sparkles className='size-3.5' aria-hidden />
                      )}
                      {isGenerating
                        ? t(
                            'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.generating'
                          )
                        : t(
                            'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.generateFromSources'
                          )}
                    </Button>
                  </div>
                  <FormControl>
                    <Textarea
                      rows={5}
                      placeholder={t(
                        'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.placeholder'
                      )}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <p className='text-xs text-muted-foreground'>
                    {t(
                      'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.helper'
                    )}
                  </p>
                  <FormMessage>
                    {form.formState.errors.description?.message ===
                    DESCRIPTION_REQUIRED
                      ? t(
                          'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.validationRequired'
                        )
                      : null}
                  </FormMessage>
                </FormItem>
              )}
            />
            <DialogFooter className='gap-2'>
              <Button
                type='button'
                variant='outline'
                className='min-w-24'
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t(
                  'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.cancel'
                )}
              </Button>
              <Button type='submit' disabled={isPending} className='min-w-24'>
                {isSaving ? (
                  <>
                    <Loader2 className='me-2 size-4 animate-spin' />
                    {t(
                      'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.saving'
                    )}
                  </>
                ) : (
                  t(
                    'v2.pages.products.detail.rohsBriefing.productDescription.editDialog.save'
                  )
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
